import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { api } from '../../lib/api'

/**
 * Alegerea punctului de lucru, cu căutare pe server.
 *
 * Sunt peste 15.000 de puncte de lucru. Un dropdown obișnuit le-ar fi adus pe toate în browser
 * și ar fi făcut ecranul inutilizabil pe telefon — exact acolo unde se folosește. De aceea
 * caută serverul, cu limită de 30 de rezultate.
 *
 * Doar unul din unsprezece puncte are adresă în evidență, așa că adresa se arată doar când
 * există. Rândul nu trebuie să pară incomplet când lipsește.
 */

export interface PunctLucru {
  id: number
  eticheta: string
  adresa: string | null
  oras: string | null
  /** Clientul de care aparține punctul, când legătura există în evidență. */
  clientId: number | null
}

interface Props {
  value: number | null
  etichetaCurenta?: string | null
  onChange: (punct: PunctLucru | null) => void
  /** Când e dat, se caută doar printre punctele acelui client. */
  clientId?: number | null
  disabled?: boolean
}

export function PunctLucruSelect({ value, etichetaCurenta, onChange, clientId, disabled }: Props) {
  const [deschis, setDeschis] = useState(false)
  const [q, setQ] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function laClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setDeschis(false)
    }
    document.addEventListener('mousedown', laClick)
    return () => document.removeEventListener('mousedown', laClick)
  }, [])

  const puncte = useQuery({
    queryKey: ['sampling', 'puncte-lucru', q, clientId],
    queryFn: async () =>
      (
        await api.get<PunctLucru[]>('/marketing/sampling/puncte-lucru', {
          params: { q: q || undefined, clientId: clientId ?? undefined },
        })
      ).data,
    enabled: deschis,
  })

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setDeschis((d) => !d)}
        className="mt-1 flex w-full items-center justify-between rounded-lg border border-b4l-border bg-white px-3 py-2 text-left text-sm disabled:opacity-50"
      >
        <span className={etichetaCurenta ? 'text-b4l-text' : 'text-b4l-text-dim'}>
          {etichetaCurenta ?? '— caută farmacia sau punctul de lucru —'}
        </span>
        <Search size={14} className="shrink-0 text-b4l-text-dim" />
      </button>

      {deschis && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-b4l-border bg-white shadow-lg">
          <div className="sticky top-0 border-b border-b4l-border bg-white p-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nume farmacie sau oraș"
              className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
            />
          </div>

          {value !== null && (
            <button
              type="button"
              onClick={() => {
                onChange(null)
                setDeschis(false)
              }}
              className="block w-full px-3 py-2 text-left text-sm text-b4l-text-muted hover:bg-b4l-bg"
            >
              — fără punct de lucru —
            </button>
          )}

          {puncte.isLoading ? (
            <p className="px-3 py-2 text-sm text-b4l-text-muted">Se caută…</p>
          ) : !puncte.data?.length ? (
            <p className="px-3 py-2 text-sm text-b4l-text-muted">Niciun punct de lucru găsit.</p>
          ) : (
            puncte.data.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange(p)
                  setDeschis(false)
                }}
                className={`block w-full px-3 py-2 text-left hover:bg-b4l-bg ${
                  p.id === value ? 'bg-b4l-blue-light' : ''
                }`}
              >
                <span className="block text-sm text-b4l-text">{p.eticheta}</span>
                {p.adresa && <span className="block text-xs text-b4l-text-muted">{p.adresa}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
